import { useState, useEffect, useCallback, useRef } from "react";
import DashboardLayout from "components/templates/DashboardLayout";
import Topnav from "components/organisms/Topnav";
import { useFormik } from "formik";
import {
  Grid,
  Row,
  Col,
  Button,
  Form,
  Input,
  InputPicker,
  Card,
  Text,
  Divider,
  Timeline,
  Notification,
  useToaster,
  ButtonToolbar,
  Toggle,
} from "rsuite";
import * as Yup from "yup";
import MDBox from "components/atoms/MDBox";

import { useAuthContext } from "context/AuthContext";
import { useDocument } from "hooks/useDocument";

// Define time options for the pickers
const values = Array.from({ length: 12 }, (_, i) =>
  (i * 5).toString().padStart(2, "0"),
);

const initialData = values.map((item) => ({
  label: item,
  value: item,
}));

const values2 = Array.from({ length: 11 }, (_, i) =>
  i.toString().padStart(2, "0"),
);

const initialData2 = values2.map((item) => ({
  label: item,
  value: item,
}));

const initialData3 = [];
initialData3.push({ label: "00", value: "00" });
initialData3.push({ label: "10", value: "10" });
initialData3.push({ label: "15", value: "15" });
initialData3.push({ label: "20", value: "20" });
initialData3.push({ label: "30", value: "30" });
initialData3.push({ label: "60", value: "60" });

// Validation schema and initial values for the formik form
const validationSchema = Yup.object().shape({
  timeFirstActuatorOpens: Yup.string()
    .required("Please select a time")
    .nullable(true),
  timeFirstActuatorCloses: Yup.string()
    .required("Please select a time")
    .nullable(true),
  oneCyclePeriod: Yup.string().required("Please select a time").nullable(true),
  timeBetweenTwoActuatorEvents: Yup.string()
    .required("Please select a time")
    .nullable(true),
});

const localDefaultSettings = {
  timeFirstActuatorOpens: "00",
  timeFirstActuatorCloses: "20",
  oneCyclePeriod: "30",
  timeBetweenTwoActuatorEvents: "10",
  openDuration: 20,
  closeDuration: 10,
  tlActuator1: [],
  tlActuator2: [],
  tlActuator3: [],
  tlActuator4: [],
};

function SetSystemSettings() {
  const { user } = useAuthContext();
  const { retrieveDoc, updateDoc, serverTimestamp } = useDocument();
  const flagFetchSettings = useRef(true);

  const pickerDataFirstActuatorOpens = initialData;
  const pickerDataTimeBetweenTwoActuatorEvents = initialData2;
  const [pickerDataFirstActuatorCloses, setPickerDataFirstActuatorCloses] =
    useState(initialData);
  const [pickerDataOneCyclePeriod, setPickerDataOneCyclePeriod] =
    useState(initialData3);

  const [modeAutoManual, setModeAutoManual] = useState(false); // false for manual, true for auto
  const [mcActuator1, setMcActuator1] = useState(false);
  const [mcActuator2, setMcActuator2] = useState(false);
  const [mcActuator3, setMcActuator3] = useState(false);
  const [mcActuator4, setMcActuator4] = useState(false);

  const [timelineActuator1, setTimelineActuator1] = useState(null);
  const [timelineActuator2, setTimelineActuator2] = useState(null);
  const [timelineActuator3, setTimelineActuator3] = useState(null);
  const [timelineActuator4, setTimelineActuator4] = useState(null);

  const [settings, setSettings] = useState(localDefaultSettings);

  // Notification
  const toaster = useToaster();
  const message = (
    <Notification type="success" header="SYSTEM SETTINGS" closable>
      <Text size="md">System settings updated successfully!</Text>
      <hr />
      <ButtonToolbar display="flex" justifyContent="flex-end">
        <Button appearance="primary" onClick={() => toaster.clear()}>
          Ok
        </Button>
      </ButtonToolbar>
    </Notification>
  );

  // Submit setting
  const submitNewSettings = async (values) => {
    /*await createDoc(
      "settings",
      {
        timeFirstActuatorOpens: values.timeFirstActuatorOpens,
        timeFirstActuatorCloses: values.timeFirstActuatorCloses,
        oneCyclePeriod: values.oneCyclePeriod,
        timeBetweenTwoActuatorEvents: values.timeBetweenTwoActuatorEvents,
        openDuration: values.openDuration,
        closeDuration: values.closeDuration,
        tlActuator1: values.tlActuator1,
        tlActuator2: values.tlActuator2,
        tlActuator3: values.tlActuator3,
        tlActuator4: values.tlActuator4,
      },
      user.uid,
      "actuators",
    );*/
    await updateDoc("settings", "actuators", {
      timeFirstActuatorOpens: values.timeFirstActuatorOpens,
      timeFirstActuatorCloses: values.timeFirstActuatorCloses,
      oneCyclePeriod: values.oneCyclePeriod,
      timeBetweenTwoActuatorEvents: values.timeBetweenTwoActuatorEvents,
      openDuration: values.openDuration,
      closeDuration: values.closeDuration,
      tlActuator1: values.tlActuator1,
      tlActuator2: values.tlActuator2,
      tlActuator3: values.tlActuator3,
      tlActuator4: values.tlActuator4,
      modifiedAt: serverTimestamp(),
      modifiedBy: user.uid,
    });

    toaster.push(message, { placement: "bottomEnd" });
    console.log("toast pushed");
  };

  // Initialize formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: settings,
    validationSchema,
    onSubmit: (values) => {
      submitNewSettings(values);
      //console.log("Submitted values:", values);
    },
  });

  const { values: formikValues } = formik;

  // InputPickerField component for rendering the label and the InputPicker with error message
  const InputPickerField = ({ name, label, data, error, ...rest }) => {
    return (
      <>
        <Col
          span={{ xs: 12, xl: 6 }}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Text size="md">{label}</Text>
        </Col>
        <Col
          span={{ xs: 12, xl: 6 }}
          display="flex"
          justifyContent="flex-start"
          alignItems="center"
        >
          <InputPicker
            {...rest}
            data={data}
            onChange={(value) => formik.setFieldValue(name, value)}
            size="md"
          />
          <span> &nbsp;</span>
          <Text size="md">min</Text>
          <Form.ErrorMessage show={!!error} placement="bottomStart">
            {error}
          </Form.ErrorMessage>
        </Col>
      </>
    );
  };

  // useEffect to update the options of the pickers based on the selected values
  const setPickerOptions = useCallback((values) => {
    // [1a] Update picker options for timeFirstActuatorCloses
    const timeOpen = parseInt(values.timeFirstActuatorOpens);
    let newData = initialData.filter((item) => parseInt(item.value) > timeOpen);
    setPickerDataFirstActuatorCloses(newData);

    // [1b] Set timeFirstActuatorCloses to "" if it's not greater than timeOpen
    if (parseInt(values.timeFirstActuatorCloses) <= timeOpen)
      values.timeFirstActuatorCloses = "";

    // [2a] Update picker options for oneCyclePeriod
    const timeClose = parseInt(values.timeFirstActuatorCloses);
    newData = initialData3.filter((item) => parseInt(item.value) > timeClose);
    setPickerDataOneCyclePeriod(newData);

    // [2b] Set oneCyclePeriod to "" if it's not greater than timeClose
    if (parseInt(values.oneCyclePeriod) <= timeClose)
      values.oneCyclePeriod = "";

    // [3] Update timeFirstActuatorOpens and timeFirstActuatorCloses based on oneCyclePeriod
    const timeOneCyclePeriod = parseInt(values.oneCyclePeriod);
    if (parseInt(values.timeFirstActuatorOpens) >= timeOneCyclePeriod)
      values.timeFirstActuatorOpens = "";
    if (parseInt(values.timeFirstActuatorCloses) >= timeOneCyclePeriod)
      values.timeFirstActuatorCloses = "";
  }, []);

  const setTimelines = useCallback((values, actuatorIndex) => {
    const timeFirstActuatorOpens = parseInt(values.timeFirstActuatorOpens);
    const timeBetweenTwoActuatorEvents = parseInt(
      values.timeBetweenTwoActuatorEvents,
    );
    const openDuration = values.openDuration;
    const closeDuration = values.closeDuration;

    let ts =
      timeFirstActuatorOpens + actuatorIndex * timeBetweenTwoActuatorEvents;

    const tsContents = [];
    let flag = true; // true for OPEN, false for CLOSE
    while (ts < 180) {
      if (flag) {
        tsContents.push({ time: ts, status: "OPEN" });
        ts += openDuration;
      } else {
        tsContents.push({ time: ts, status: "CLOSE" });
        ts += closeDuration;
      }
      flag = !flag;
    }

    const tsContentsFilt = tsContents.filter((item) => item.time >= 60);

    const offsetTimeline = tsContentsFilt.map((item) => ({
      ...item,
      time: item.time - 60,
    }));

    actuatorIndex === 0 && (values.tlActuator1 = offsetTimeline);
    actuatorIndex === 1 && (values.tlActuator2 = offsetTimeline);
    actuatorIndex === 2 && (values.tlActuator3 = offsetTimeline);
    actuatorIndex === 3 && (values.tlActuator4 = offsetTimeline);

    // Timeline components on UI
    const tlComponent = offsetTimeline.map((item, index) => (
      <Timeline.Item key={index}>
        <Text size="md" color={item.status === "OPEN" ? "blue" : "red"}>
          {item.time.toString().padStart(2, "0")} min, {item.status}
        </Text>
      </Timeline.Item>
    ));

    return tlComponent;
  }, []);

  const fetchSettings = useCallback(async () => {
    const retrievedSettings =
      user.uid && (await retrieveDoc("settings", "actuators"));
    const data = !!retrievedSettings.data
      ? retrievedSettings.data
      : localDefaultSettings;

    setSettings({
      timeFirstActuatorOpens: data.timeFirstActuatorOpens,
      timeFirstActuatorCloses: data.timeFirstActuatorCloses,
      oneCyclePeriod: data.oneCyclePeriod,
      timeBetweenTwoActuatorEvents: data.timeBetweenTwoActuatorEvents,
      openDuration: data.openDuration,
      closeDuration: data.closeDuration,
      tlActuator1: data.tlActuator1,
      tlActuator2: data.tlActuator2,
      tlActuator3: data.tlActuator3,
      tlActuator4: data.tlActuator4,
    });

    setMcActuator1(data.mcActuator1);
    setMcActuator2(data.mcActuator2);
    setMcActuator3(data.mcActuator3);
    setMcActuator4(data.mcActuator4);
    setModeAutoManual(data.modeAutoManual);

    return retrievedSettings;
  }, [retrieveDoc, user.uid]);

  useEffect(() => {
    // Get the latest settings from the database and update the formik values and timelines accordingly
    if (flagFetchSettings.current) {
      fetchSettings();
      flagFetchSettings.current = false;
    }

    // [1] Update picker options whenever formik values change
    setPickerOptions(formikValues);

    // [2] Recalculate openDuration and closeDuration
    const timeOpen = parseInt(formikValues.timeFirstActuatorOpens);
    const timeClose = parseInt(formikValues.timeFirstActuatorCloses);
    isNaN(timeOpen) || isNaN(timeClose)
      ? (formikValues.openDuration = -1)
      : (formikValues.openDuration = timeClose - timeOpen);
    isNaN(timeClose) || isNaN(formikValues.oneCyclePeriod)
      ? (formikValues.closeDuration = -1)
      : (formikValues.closeDuration =
          parseInt(formikValues.oneCyclePeriod) - timeClose);

    // [3]
    const tlComponentActuator1 = setTimelines(formikValues, 0);
    setTimelineActuator1(
      <Timeline align="left" isItemActive={(index) => index === -1} endless>
        {tlComponentActuator1}
      </Timeline>,
    );
    const tlComponentActuator2 = setTimelines(formikValues, 1);
    setTimelineActuator2(
      <Timeline align="left" isItemActive={(index) => index === -1} endless>
        {tlComponentActuator2}
      </Timeline>,
    );
    const tlComponentActuator3 = setTimelines(formikValues, 2);
    setTimelineActuator3(
      <Timeline align="left" isItemActive={(index) => index === -1} endless>
        {tlComponentActuator3}
      </Timeline>,
    );
    const tlComponentActuator4 = setTimelines(formikValues, 3);
    setTimelineActuator4(
      <Timeline align="left" isItemActive={(index) => index === -1} endless>
        {tlComponentActuator4}
      </Timeline>,
      tlComponentActuator4,
    );
  }, [formikValues, setPickerOptions, setTimelines, fetchSettings]);

  const updateSettingsDoc = useCallback(
    async (key, value) => {
      await updateDoc("settings", "actuators", {
        [key]: value,
        modifiedAt: serverTimestamp(),
        modifiedBy: user.uid,
      });
    },
    [updateDoc, serverTimestamp, user.uid],
  );

  return (
    <DashboardLayout>
      <Topnav title="System Settings" />
      <MDBox mb={4} />

      <Grid fluid>
        <Row gutter={[9, 18]}>
          <Col span={{ xs: 24 }}>
            <Card size="md" shaded>
              <Card.Header
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Toggle
                  color="green"
                  size="lg"
                  checkedChildren="Auto"
                  unCheckedChildren="Manual"
                  checked={modeAutoManual}
                  onChange={(checked) => {
                    setModeAutoManual(checked);
                    updateSettingsDoc("modeAutoManual", checked);
                  }}
                ></Toggle>
                <span style={{ marginLeft: "20px" }} />
                <Text size="lg" style={{ textAlign: "left", width: "100%" }}>
                  {modeAutoManual
                    ? "Automated Control Mode"
                    : "Manual Control Mode"}
                </Text>
              </Card.Header>
              <Divider />
              <Card.Body>
                {modeAutoManual ? (
                  <Form onSubmit={formik.handleSubmit}>
                    <Form.Stack fluid>
                      <Grid fluid>
                        <Row gutter={[24, 24]}>
                          <InputPickerField
                            name="timeFirstActuatorOpens"
                            label="Time first actuator opens"
                            data={pickerDataFirstActuatorOpens}
                            value={formik.values.timeFirstActuatorOpens}
                            error={formik.errors.timeFirstActuatorOpens}
                          />
                          <InputPickerField
                            name="timeFirstActuatorCloses"
                            label="Time first actuator closes"
                            data={pickerDataFirstActuatorCloses}
                            value={formik.values.timeFirstActuatorCloses}
                            error={formik.errors.timeFirstActuatorCloses}
                          />
                          <InputPickerField
                            name="oneCyclePeriod"
                            label="One cycle period"
                            data={pickerDataOneCyclePeriod}
                            value={formik.values.oneCyclePeriod}
                            error={formik.errors.oneCyclePeriod}
                          />
                          <InputPickerField
                            name="timeBetweenTwoActuatorEvents"
                            label="Time between two sequential actuator events (O-O or C-C)"
                            data={pickerDataTimeBetweenTwoActuatorEvents}
                            value={formik.values.timeBetweenTwoActuatorEvents}
                            error={formik.errors.timeBetweenTwoActuatorEvents}
                          />
                          <Col
                            span={{ xs: 24 }}
                            display="flex"
                            justifyContent="flex-end"
                            alignItems="center"
                          >
                            <Button appearance="primary" type="submit">
                              Submit
                            </Button>
                          </Col>
                          <Col span={{ xs: 24, lg: 12 }}>
                            <Card size="md" shaded>
                              <Card.Header>
                                <Text
                                  size="lg"
                                  style={{ textAlign: "left", width: "100%" }}
                                >
                                  Actuator 1
                                </Text>
                                <Divider style={{ margin: "3px 0" }} />
                              </Card.Header>
                              <Card.Body
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                              >
                                {timelineActuator1}
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col span={{ xs: 24, lg: 12 }}>
                            <Card size="md" shaded>
                              <Card.Header>
                                <Text
                                  size="lg"
                                  style={{ textAlign: "left", width: "100%" }}
                                >
                                  Actuator 2
                                </Text>
                                <Divider style={{ margin: "3px 0" }} />
                              </Card.Header>
                              <Card.Body
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                              >
                                {timelineActuator2}
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col span={{ xs: 24, lg: 12 }}>
                            <Card size="md" shaded>
                              <Card.Header>
                                <Text
                                  size="lg"
                                  style={{ textAlign: "left", width: "100%" }}
                                >
                                  Actuator 3
                                </Text>
                                <Divider style={{ margin: "3px 0" }} />
                              </Card.Header>
                              <Card.Body
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                              >
                                {timelineActuator3}
                              </Card.Body>
                            </Card>
                          </Col>
                          <Col span={{ xs: 24, lg: 12 }}>
                            <Card size="md" shaded>
                              <Card.Header>
                                <Text
                                  size="lg"
                                  style={{ textAlign: "left", width: "100%" }}
                                >
                                  Actuator 4
                                </Text>
                                <Divider style={{ margin: "3px 0" }} />
                              </Card.Header>
                              <Card.Body
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                              >
                                {timelineActuator4}
                              </Card.Body>
                            </Card>
                          </Col>
                        </Row>
                      </Grid>
                    </Form.Stack>
                  </Form>
                ) : (
                  <Grid fluid>
                    <Row gutter={[24, 24]}>
                      <Col
                        span={{ xs: 24 }}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ paddingLeft: "40px" }}
                      >
                        <Text size="md">Actuator 1</Text>
                        <span style={{ marginLeft: "60px" }} />
                        <Toggle
                          color="green"
                          size="md"
                          checked={mcActuator1}
                          onChange={(checked) => {
                            setMcActuator1(checked);
                            updateSettingsDoc("mcActuator1", checked);
                          }}
                        >
                          {mcActuator1 ? "ON" : "OFF"}
                        </Toggle>
                      </Col>
                      <Col
                        span={{ xs: 24 }}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ paddingLeft: "40px" }}
                      >
                        <Text size="md">Actuator 2</Text>
                        <span style={{ marginLeft: "60px" }} />
                        <Toggle
                          color="green"
                          size="md"
                          checked={mcActuator2}
                          onChange={(checked) => {
                            setMcActuator2(checked);
                            updateSettingsDoc("mcActuator2", checked);
                          }}
                        >
                          {mcActuator2 ? "ON" : "OFF"}
                        </Toggle>
                      </Col>
                      <Col
                        span={{ xs: 24 }}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ paddingLeft: "40px" }}
                      >
                        <Text size="md">Actuator 3</Text>
                        <span style={{ marginLeft: "60px" }} />
                        <Toggle
                          color="green"
                          size="md"
                          checked={mcActuator3}
                          onChange={(checked) => {
                            setMcActuator3(checked);
                            updateSettingsDoc("mcActuator3", checked);
                          }}
                        >
                          {mcActuator3 ? "ON" : "OFF"}
                        </Toggle>
                      </Col>
                      <Col
                        span={{ xs: 24 }}
                        display="flex"
                        justifyContent="flex-start"
                        alignItems="center"
                        style={{ paddingLeft: "40px" }}
                      >
                        <Text size="md">Actuator 4</Text>
                        <span style={{ marginLeft: "60px" }} />
                        <Toggle
                          color="green"
                          size="md"
                          checked={mcActuator4}
                          onChange={(checked) => {
                            setMcActuator4(checked);
                            updateSettingsDoc("mcActuator4", checked);
                          }}
                        >
                          {mcActuator4 ? "ON" : "OFF"}
                        </Toggle>
                      </Col>
                    </Row>
                  </Grid>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col span={{ xs: 24 }}>
            <Card size="md" shaded>
              <Card.Header
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
              >
                <Text size="lg" style={{ textAlign: "left", width: "100%" }}>
                  Alert Messages
                </Text>
              </Card.Header>
              <Divider />
              <Card.Body>
                <Grid fluid>
                  <Row
                    gutter={[24, 24]}
                    style={{ paddingLeft: "60px", paddingRight: "60px" }}
                  >
                    <Col
                      span={{ xs: 6, xl: 6 }}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Text size="md">No Draining Message</Text>
                    </Col>
                    <Col
                      span={{ xs: 18, xl: 18 }}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Input
                        placeholder="No draining detected, valve open, no flow."
                        size="lg"
                        value="No draining detected, valve open, no flow."
                      />
                    </Col>
                    <Col
                      span={{ xs: 6, xl: 6 }}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Text size="md">Unintended Draining Message</Text>
                    </Col>
                    <Col
                      span={{ xs: 18, xl: 18 }}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Input
                        placeholder="Unintended draining, valve close, got flow."
                        size="lg"
                        value="Unintended draining, valve close, got flow."
                      />
                    </Col>
                    <Col
                      span={{ xs: 6, xl: 6 }}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Text size="md">Flood Alert Activated</Text>
                    </Col>
                    <Col
                      span={{ xs: 18, xl: 18 }}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Input
                        placeholder="Basement level HH. All draining stop."
                        size="lg"
                        value="Basement level HH. All draining stop."
                      />
                    </Col>
                    <Col
                      span={{ xs: 6, xl: 6 }}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Text size="md">Flood Alert Deactivated</Text>
                    </Col>
                    <Col
                      span={{ xs: 18, xl: 18 }}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Input
                        placeholder="Basement level OK. HH deactivated. Resume auto sequence."
                        size="lg"
                        value="Basement level OK. HH deactivated. Resume auto sequence."
                      />
                    </Col>
                  </Row>
                </Grid>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Grid>

      <Grid fluid>
        <Row gutter={[9, 18]}></Row>
      </Grid>
      {/*message*/}
    </DashboardLayout>
  );
}

export default SetSystemSettings;
